import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  List,
  ListItem,
  ListItemText,
  Chip,
  LinearProgress,
  Alert,
  useTheme,
} from '@mui/material';
import { Grid } from '../components/common/TypedGrid';
import {
  TrendingUp,
  TrendingDown,
  People,
  Assignment,
  AttachMoney,
  LocalShipping,
  DateRange,
  Refresh,
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
} from 'recharts';
import { useAdmin } from '../contexts/AdminContext';
import adminService from '../services/adminService';
// Issue #243: Import Firebase for real-time listeners
import { collection, onSnapshot } from 'firebase/firestore';
import { db } from '../config/firebase';
import { PageHeader, StatCard } from '../components/common';

interface AnalyticsData {
  revenue: Array<{ month: string; revenue: number; tasks: number }>;
  tasksByStatus: Array<{ status: string; count: number; color: string }>;
  topNinjas: Array<{ name: string; tasks: number; revenue: number }>;
  customerGrowth: Array<{ month: string; customers: number }>;
  performanceMetrics: {
    avgDeliveryTime: number;
    customerSatisfaction: number;
    taskCompletionRate: number;
    revenueGrowth: number;
  };
}

const Analytics: React.FC = () => {
  const theme = useTheme();
  const { dashboardStats } = useAdmin();
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('6months');
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [analyticsData, setAnalyticsData] = useState<AnalyticsData>({
    revenue: [],
    tasksByStatus: [],
    topNinjas: [],
    customerGrowth: [],
    performanceMetrics: {
      avgDeliveryTime: 0,
      customerSatisfaction: 0,
      taskCompletionRate: 0,
      revenueGrowth: 0,
    },
  });

  useEffect(() => {
    loadAnalyticsData();
  }, [timeRange]);

  // Issue #243: Set up real-time listeners for ninja_wallets and tasks to auto-update analytics
  useEffect(() => {
    console.log('🔄 Issue #243: Setting up real-time listeners for analytics');

    // Listen to ninja_wallets collection for earnings updates
    const walletsUnsubscribe = onSnapshot(
      collection(db, 'ninja_wallets'),
      (snapshot) => {
        console.log('💰 Issue #243: Ninja wallets updated, reloading analytics');
        loadAnalyticsData();
      },
      (error) => {
        console.error('❌ Issue #243: Error in ninja_wallets listener:', error);
      }
    );

    // Listen to tasks collection for task completion updates
    const tasksUnsubscribe = onSnapshot(
      collection(db, 'tasks'),
      (snapshot) => {
        console.log('📋 Issue #243: Tasks updated, reloading analytics');
        loadAnalyticsData();
      },
      (error) => {
        console.error('❌ Issue #243: Error in tasks listener:', error);
      }
    );

    // Cleanup listeners on unmount
    return () => {
      console.log('🔄 Issue #243: Cleaning up real-time listeners');
      walletsUnsubscribe();
      tasksUnsubscribe();
    };
  }, [timeRange]); // Re-setup listeners if timeRange changes

  const loadAnalyticsData = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      // Get dynamic analytics data based on time range
      const data = await adminService.getAnalyticsData(timeRange);
      setAnalyticsData(data);
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Error loading analytics data:', error);
      // Previously this substituted hard-coded sample figures (fake revenue,
      // fake ninja names) and stamped them with a fresh "last updated" time,
      // so a failed load was indistinguishable from real business data.
      // Surface the failure instead and show nothing rather than something
      // untrue.
      setAnalyticsData({
        revenue: [],
        tasksByStatus: [],
        topNinjas: [],
        customerGrowth: [],
        performanceMetrics: {
          avgDeliveryTime: 0,
          customerSatisfaction: 0,
          taskCompletionRate: 0,
          revenueGrowth: 0,
        },
      });
      setLastUpdated(null);
      setLoadError(
        'We could not load analytics data. The figures below are not available — check your connection and try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value: number) => `₹${value.toLocaleString()}`;

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" fontWeight={700}>
            Analytics Dashboard
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <FormControl sx={{ minWidth: 150 }} disabled>
              <InputLabel>Time Range</InputLabel>
              <Select
                value={timeRange}
                label="Time Range"
              >
                <MenuItem value="1month">Last Month</MenuItem>
                <MenuItem value="3months">Last 3 Months</MenuItem>
                <MenuItem value="6months">Last 6 Months</MenuItem>
                <MenuItem value="1year">Last Year</MenuItem>
              </Select>
            </FormControl>
            <Button
              startIcon={<Refresh />}
              disabled
              variant="outlined"
            >
              Refresh
            </Button>
          </Box>
        </Box>
        <LinearProgress sx={{ mb: 2 }} />
        <Typography sx={{ textAlign: 'center', color: 'text.secondary' }}>
          Loading analytics data for {timeRange === '1month' ? 'last month' : timeRange === '3months' ? 'last 3 months' : timeRange === '6months' ? 'last 6 months' : 'last year'}...
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title="Analytics"
        subtitle={lastUpdated ? `Last updated: ${lastUpdated.toLocaleString()}` : 'Performance across the platform'}
        actions={
          <>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Time Range</InputLabel>
              <Select
                value={timeRange}
                label="Time Range"
                onChange={(e) => setTimeRange(e.target.value)}
              >
                <MenuItem value="1month">Last Month</MenuItem>
                <MenuItem value="3months">Last 3 Months</MenuItem>
                <MenuItem value="6months">Last 6 Months</MenuItem>
                <MenuItem value="1year">Last Year</MenuItem>
              </Select>
            </FormControl>
            <Button startIcon={<Refresh />} onClick={loadAnalyticsData} variant="outlined">
              Refresh
            </Button>
          </>
        }
      />

      {loadError && (
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" onClick={loadAnalyticsData}>
              Retry
            </Button>
          }
        >
          {loadError}
        </Alert>
      )}

      {/* Performance Metrics */}
      <Grid container spacing={{ xs: 1.5, md: 3 }} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          <StatCard
            label="Avg Task Completion Time"
            value={`${analyticsData.performanceMetrics.avgDeliveryTime} hrs`}
            icon={<LocalShipping />}
            tone="info"
            delta={`${Math.abs(Math.round((analyticsData.performanceMetrics.avgDeliveryTime - 2.5) * 10))}%`}
            deltaDirection={analyticsData.performanceMetrics.avgDeliveryTime < 2.5 ? 'up' : 'down'}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            label="Customer Satisfaction"
            value={`${analyticsData.performanceMetrics.customerSatisfaction}/5`}
            icon={<People />}
            tone="success"
            delta={`${Math.round((analyticsData.performanceMetrics.customerSatisfaction - 4.0) * 25)}%`}
            deltaDirection={analyticsData.performanceMetrics.customerSatisfaction > 4.0 ? 'up' : 'down'}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            label="Task Completion"
            value={`${analyticsData.performanceMetrics.taskCompletionRate}%`}
            icon={<Assignment />}
            tone="warning"
            delta={`${Math.round(analyticsData.performanceMetrics.taskCompletionRate - 85)}%`}
            deltaDirection={analyticsData.performanceMetrics.taskCompletionRate > 85 ? 'up' : 'down'}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatCard
            label="Revenue Growth"
            value={`${analyticsData.performanceMetrics.revenueGrowth}%`}
            icon={<AttachMoney />}
            tone="primary"
            delta={`${Math.abs(analyticsData.performanceMetrics.revenueGrowth)}%`}
            deltaDirection={analyticsData.performanceMetrics.revenueGrowth > 0 ? 'up' : 'down'}
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Revenue Trend */}
        <Grid item xs={12} lg={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Revenue & Task Trends
              </Typography>
              <ResponsiveContainer width="100%" height={400}>
                <AreaChart data={analyticsData.revenue}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis yAxisId="left" />
                  <YAxis yAxisId="right" orientation="right" />
                  <Tooltip 
                    formatter={(value, name) => [
                      name === 'revenue' ? formatCurrency(value as number) : value,
                      name === 'revenue' ? 'Revenue' : 'Tasks'
                    ]}
                  />
                  <Area
                    yAxisId="left"
                    type="monotone"
                    dataKey="revenue"
                    stackId="1"
                    stroke={theme.palette.primary.main}
                    fill={theme.palette.primary.main}
                    fillOpacity={0.6}
                  />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="tasks"
                    stroke={theme.palette.text.primary}
                    strokeWidth={3}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Task Status Distribution */}
        <Grid item xs={12} lg={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Task Distribution
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={analyticsData.tasksByStatus}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={120}
                    paddingAngle={5}
                    dataKey="count"
                  >
                    {analyticsData.tasksByStatus.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <Box sx={{ mt: 2 }}>
                {analyticsData.tasksByStatus.map((item) => (
                  <Box key={item.status} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        bgcolor: item.color,
                        mr: 1,
                      }}
                    />
                    <Typography variant="body2" sx={{ flexGrow: 1 }}>
                      {item.status}
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {item.count}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Customer Growth */}
        <Grid item xs={12} lg={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Customer Growth
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={analyticsData.customerGrowth}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Bar
                    dataKey="customers"
                    fill={theme.palette.primary.main}
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Top Performing Ninjas */}
        <Grid item xs={12} lg={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Top Performing Ninjas
              </Typography>
              <List>
                {analyticsData.topNinjas.map((ninja, index) => (
                  <ListItem key={ninja.name} sx={{ px: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: '50%',
                          bgcolor: index === 0 ? 'gold' : index === 1 ? 'silver' : index === 2 ? '#CD7F32' : 'grey.300',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mr: 2,
                          color: 'white',
                          fontWeight: 'bold',
                        }}
                      >
                        {index + 1}
                      </Box>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="subtitle2" fontWeight={600}>
                          {ninja.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {ninja.tasks} tasks • {formatCurrency(ninja.revenue)}
                        </Typography>
                      </Box>
                      <Chip
                        label={`${ninja.tasks} tasks`}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </Box>
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Analytics;